import { create } from 'zustand';

// UI shell state — sidebar drawer (mobile) + collapsed/icon-only (desktop).
// Persisted across reloads via localStorage so customer doesn't lose their
// preference. Mobile drawer state is NOT persisted (always closed on load).

const STORAGE_KEY = 'pw_sidebar_collapsed';

const readInitialCollapsed = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY) === 'true';
};

interface UIState {
  /** Mobile drawer overlay open/closed. Always false on initial load. */
  mobileSidebarOpen: boolean;
  /** Desktop sidebar collapsed to icons-only. Persisted. */
  sidebarCollapsed: boolean;
  /** Guide / Troubleshooting modal open/closed. Always false on load. */
  guideOpen: boolean;

  openMobileSidebar: () => void;
  closeMobileSidebar: () => void;
  toggleMobileSidebar: () => void;

  openGuide: () => void;
  closeGuide: () => void;

  toggleSidebarCollapsed: () => void;
  setSidebarCollapsed: (v: boolean) => void;
}

export const useUIStore = create<UIState>((set) => ({
  mobileSidebarOpen: false,
  sidebarCollapsed: readInitialCollapsed(),
  guideOpen: false,

  openMobileSidebar: () => set({ mobileSidebarOpen: true }),
  closeMobileSidebar: () => set({ mobileSidebarOpen: false }),
  toggleMobileSidebar: () => set((s) => ({ mobileSidebarOpen: !s.mobileSidebarOpen })),

  openGuide: () => set({ guideOpen: true }),
  closeGuide: () => set({ guideOpen: false }),

  toggleSidebarCollapsed: () =>
    set((s) => {
      const next = !s.sidebarCollapsed;
      try {
        window.localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* storage may be unavailable in some embed contexts — silent fail */
      }
      return { sidebarCollapsed: next };
    }),

  setSidebarCollapsed: (v) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, String(v));
    } catch {
      /* */
    }
    set({ sidebarCollapsed: v });
  },
}));
